'use client';

import { useCallback, useMemo, useState, useEffect } from 'react';
import useSearchParams from '@/hooks/useSearchParams';
import { colors } from '@/constants/filter-colors';
import SearchIcon from '@/components/ui/icons/search-icon';
import { useLocale, useTranslations } from 'next-intl';
import { useRouter } from 'next/navigation';
import { usePathname } from '@/i18n/navigation';

let isGoToInitPage = true;

const Filter = ({ data: filter, isColor = false, isMobile }) => {
  const { getFilterObj, setFilterQueryHandler } = useSearchParams();
  const [searchTerm, setSearchTerm] = useState('');
  const locale = useLocale();
  const { push } = useRouter();
  const pathname = usePathname();

  const t = useTranslations('common');

  const [filtersData, setFiltersData] = useState(filter?.list);

  const checkedFilters = useMemo(
    () => getFilterObj?.[filter.code]?.split(',') || [],
    [getFilterObj, filter.code]
  );
  const handleFilter = useCallback(
    (key) => {
      const isChecked = checkedFilters.includes(String(key));

      const updatedFilters = isChecked
        ? checkedFilters.filter((el) => el !== String(key))
        : [...checkedFilters, String(key)];

      setFilterQueryHandler({
        [filter.code]: updatedFilters.length
          ? updatedFilters.join(',')
          : undefined,
      });
      isGoToInitPage = true;
    },
    [checkedFilters, filter.code, setFilterQueryHandler]
  );

  // go to the init page
  useEffect(() => {
    if (Object.keys(getFilterObj || {}).length == 0 && isGoToInitPage) {
      push(pathname);
      isGoToInitPage = false;
    }
  }, [getFilterObj, pathname, push]);

  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFiltersData(filter?.list);
    } else {
      const lowercasedTerm = searchTerm?.toLowerCase();
      const filtered = filter?.list?.filter(({ Label }) =>
        Label?.toLowerCase()?.includes(lowercasedTerm)
      );
      setFiltersData(filtered);
    }
  }, [searchTerm, filter?.list]);

  return (
    <div
      className={`group rounded-lg border border-[#F6F6F7] px-5 py-3 ${isMobile && 'px-2.75'}`}
    >
      <h3
        className={`text-lg font-semibold ${isMobile && 'text-xs font-bold'}`}
      >
        {filter?.title}
      </h3>

      {(filter?.code === 'brands' || filter?.code === 'categories') && (
        <div className='relative mt-3 mb-4'>
          <input
            type='text'
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder={t('search')}
            className={`focus:ring-primary w-full rounded-lg border border-[#F6F6F7] px-10 py-1.5 text-sm focus:outline-none ${isMobile && '!px-8 !text-xs'}`}
          />
          <div
            className={`absolute top-1/2 ${locale === 'ar' ? 'right-3' : 'left-3'} -translate-y-1/2`}
          >
            <SearchIcon
              className={`size-4 text-gray-400 ${isMobile && '!size-3'}`}
            />
          </div>
        </div>
      )}

      <ul className='mt-2.25 leading-relaxed text-gray-700 md:mt-4'>
        {filtersData?.map(({ Key, Label, DocCount }) => {
          const isChecked = checkedFilters.includes(String(Key));

          return (
            <li
              key={Key}
              className='hover:text-primary mb-3 flex cursor-pointer items-center gap-4'
              onClick={() => handleFilter(Key)}
              role='checkbox'
              aria-checked={isChecked}
            >
              <input
                type='checkbox'
                className='border-primary checked:bg-primary size-4 shrink-0 cursor-pointer appearance-none rounded-sm border max-md:size-3.5'
                checked={isChecked}
                readOnly
              />
              {isColor && (
                <span
                  className={`border-border h-4 w-4 rounded-sm border`}
                  style={{ backgroundColor: colors[Key] }}
                ></span>
              )}
              <label className={`cursor-pointer ${isMobile && 'text-xs'}`}>
                {Label} ({DocCount})
              </label>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default Filter;
